import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import Drawer from '../Drawer';
import { SelectDropdown } from '../SelectDropdown';
import { TrashIcon } from '@heroicons/react/24/outline';

const safeString = (v) => (v === null || v === undefined ? '' : String(v));

const makeId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto?.randomUUID) return crypto.randomUUID();
  } catch (e) {
    console.error(e);
  }
  return `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const ItemTypes = {
  CHILD: 'template-child'
};

// Draggable Child Item Component
const DraggableChildItem = ({ 
  child, 
  categoryId, 
  index, 
  updateChildField, 
  deleteChild, 
  moveChild,
  isSaving 
}) => {
  const containerRef = useRef(null);
  const dragHandleRef = useRef(null);

  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemTypes.CHILD,
    canDrag: () => !isSaving,
    item: () => {
      return { 
        index, 
        childId: child.id, 
        categoryId 
      };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ItemTypes.CHILD,
    hover: (draggedItem) => {
      if (!draggedItem || draggedItem.index === index) return;
      if (draggedItem.categoryId !== categoryId) return;
      moveChild(categoryId, draggedItem.index, index);
      draggedItem.index = index;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  });

  drop(containerRef);
  drag(!isSaving ? dragHandleRef : containerRef);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const showDropIndicator = isOver && canDrop && !isSaving;

  return (
    <div
      ref={containerRef}
      className={`
        border rounded-md p-3 transition-all duration-200
        ${isDragging ? 'opacity-50 border-blue-300' : 'border-gray-200'}
        ${showDropIndicator ? 'border-blue-500 border-2 shadow-md' : 'hover:border-gray-300 hover:shadow-sm'}
      `}
    >
      <div className="flex flex-row justify-between gap-3">
        <div 
          ref={dragHandleRef}
          className="cursor-move text-gray-400 hover:text-gray-600 flex-shrink-0 self-center"
          aria-label="Drag to reorder"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </div>
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-600 mb-1">Code</label>
          <input
            type="text"
            value={child.code || ''}
            onChange={(e) => updateChildField(categoryId, child.id, 'code', e.target.value)}
            className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm"
            disabled={isSaving}
          />
        </div>
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
          <input
            type="text"
            value={child.name || ''}
            onChange={(e) => updateChildField(categoryId, child.id, 'name', e.target.value)}
            className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm"
            disabled={isSaving}
          />
        </div>
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
          <SelectDropdown
            options={[
              { label: "Editable", value: "EDITABLE" },
              { label: "Calculated", value: "CALCULATED" },
              { label: "Forecast Value", value: "FORECAST_VALUE" },
            ]}
            value={child.type || ''}
            itemValueClassname="px-2 py-1 text-sm text-left"
            selectedValueClassname="whitespace-nowrap overflow-hidden text-ellipsis"
            selectClassname="!bg-white !px-3 !py-2 !border-input"
            onChange={(value) => updateChildField(categoryId, child.id, 'type', value)}
            disabled={isSaving}
          />
        </div>
        <div className="flex justify-end items-center">
          <button
            type="button"
            onClick={() => deleteChild(categoryId, child.id)}
            className="text-sm text-red-600 hover:text-red-800"
            disabled={isSaving}
          >
            <TrashIcon className="h-5 w-5 text-red-600" />
          </button>
        </div>
      </div>
    </div>
  );
};

export const TemplateCreateEditSidebar = ({
  embedded = false,
  isOpen,
  onClose,
  mode = 'create',
  template,
  categoriesToRender = [],
  onSave,
  isSaving = false,
  onRegisterSubmit,
  onCanSaveChange,
}) => {
  const initialSnapshotRef = useRef('');
  const submitRef = useRef(null);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState(new Set());

  const masterCategories = useMemo(() => {
    const rows = Array.isArray(categoriesToRender) ? categoriesToRender : [];
    return rows
      .map((c) => ({ id: c?.id, name: c?.name }))
      .filter((c) => c?.id);
  }, [categoriesToRender]);

  const templateCategories = useMemo(() => {
    const items = template?.items && typeof template.items === 'object' ? template.items : {};
    const cats = Array.isArray(items?.categories) ? items.categories : [];
    return cats
      .map((c) => ({
        categoryId: c?.categoryId ?? c?.category_id,
        children: Array.isArray(c?.children) ? c.children : [],
      }))
      .filter((c) => c?.categoryId);
  }, [template]);

  const buildInitialState = useMemo(() => {
    if (mode === 'edit' && template) {
      return {
        name: safeString(template?.name || ''),
        categories: masterCategories.map((mc) => {
          const existing = templateCategories.find((c) => String(c.categoryId) === String(mc.id));
          const existingChildren = existing?.children || [];
          // Sort by position if available, otherwise maintain order
          const sortedChildren = [...existingChildren].sort((a, b) => {
            const posA = a?.position ?? a?.order ?? 0;
            const posB = b?.position ?? b?.order ?? 0;
            return posA - posB;
          });
          return {
            categoryId: mc.id,
            categoryName: mc.name,
            children: sortedChildren.map((ch, idx) => ({
              id: ch?.id ?? makeId(),
              code: safeString(ch?.code || ''),
              name: safeString(ch?.name || ''),
              type: safeString(ch?.type || ''),
              position: ch?.position ?? idx + 1,
            })),
          };
        }),
      };
    }

    // create mode
    return {
      name: '',
      categories: masterCategories.map((mc) => ({
        categoryId: mc.id,
        categoryName: mc.name,
        children: [],
      })),
    };
  }, [mode, template, masterCategories, templateCategories]);

  const [localState, setLocalState] = useState(buildInitialState);

  // Re-init when opening or when template/categories change
  useEffect(() => {
    if (!isOpen) return;
    setLocalState(buildInitialState);
    setExpandedCategoryIds(new Set());
    initialSnapshotRef.current = JSON.stringify(buildInitialState);
  }, [isOpen, buildInitialState]);

  const hasChanges = useMemo(() => {
    if (!isOpen) return false;
    const snap = initialSnapshotRef.current || '';
    return JSON.stringify(localState) !== snap;
  }, [isOpen, localState]);

  const canSave = useMemo(() => {
    const nameOk = safeString(localState?.name).trim().length > 0;
    return nameOk && hasChanges && !isSaving;
  }, [localState?.name, hasChanges, isSaving]);

  const title = mode === 'edit' ? 'Edit Template' : 'Create Template';

  const toggleExpanded = (categoryId) => {
    setExpandedCategoryIds((prev) => {
      const next = new Set(prev);
      const key = String(categoryId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const updateCategoryChildren = useCallback((categoryId, updater) => {
    setLocalState((prev) => {
      const categories = Array.isArray(prev?.categories) ? prev.categories : [];
      const nextCategories = categories.map((c) => {
        if (String(c.categoryId) !== String(categoryId)) return c;
        const children = Array.isArray(c.children) ? c.children : [];
        const nextChildren = updater(children);
        return { ...c, children: nextChildren };
      });
      return { ...prev, categories: nextCategories };
    });
  }, []);

  const addChild = (categoryId) => {
    updateCategoryChildren(categoryId, (children) => {
      const maxPosition = children.length > 0 
        ? Math.max(...children.map(ch => ch?.position ?? 0))
        : 0;
      return [
        ...children,
        { id: makeId(), code: '', name: '', type: 'EDITABLE', position: maxPosition + 1 },
      ];
    });
    setExpandedCategoryIds((prev) => new Set(prev).add(String(categoryId)));
  };

  const moveChild = useCallback((categoryId, dragIndex, hoverIndex) => {
    updateCategoryChildren(categoryId, (children) => {
      const sorted = [...children].sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0));
      const [removed] = sorted.splice(dragIndex, 1);
      sorted.splice(hoverIndex, 0, removed);
      // Reassign positions
      return sorted.map((ch, idx) => ({
        ...ch,
        position: idx + 1
      }));
    });
  }, [updateCategoryChildren]);

  const updateChildField = (categoryId, childId, field, value) => {
    updateCategoryChildren(categoryId, (children) =>
      children.map((ch) => (String(ch.id) === String(childId) ? { ...ch, [field]: value } : ch))
    );
  };

  const deleteChild = (categoryId, childId) => {
    updateCategoryChildren(categoryId, (children) => {
      const filtered = children.filter((ch) => String(ch.id) !== String(childId));
      // Reassign positions after deletion
      return filtered.map((ch, idx) => ({
        ...ch,
        position: idx + 1
      }));
    });
  };

  const handleSave = async () => {
    if (!canSave) return;
    const name = safeString(localState?.name).trim();
    const categories = (localState?.categories || []).map((c) => {
      // Sort children by position before saving
      const sortedChildren = [...(c.children || [])].sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0));
      return {
        categoryId: c.categoryId,
        categoryName: c.categoryName,
        children: sortedChildren.map((ch, idx) => ({
          id: ch.id,
          code: safeString(ch.code).trim(),
          name: safeString(ch.name).trim(),
          type: safeString(ch.type).trim(),
          position: ch.position ?? idx + 1,
        })),
      };
    });
    await onSave?.({ name, categories });
  };

  useEffect(() => {
    submitRef.current = handleSave;
  }, [handleSave]);

  useEffect(() => {
    if (!onRegisterSubmit) return;
    // Register a stable callback that always calls the latest submitRef
    onRegisterSubmit(() => submitRef.current?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRegisterSubmit]);

  useEffect(() => {
    onCanSaveChange?.(canSave);
  }, [canSave, onCanSaveChange]);

  const content = (
    <div className="px-0 py-4">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
          <input
            type="text"
            value={localState?.name || ''}
            onChange={(e) => setLocalState((p) => ({ ...p, name: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter template name"
            disabled={isSaving}
          />
        </div>

        <div className="space-y-3">
          {(masterCategories || []).map((mc) => {
            const catState = (localState?.categories || []).find((c) => String(c.categoryId) === String(mc.id));
            const children = Array.isArray(catState?.children) ? catState.children : [];
            const isExpanded = expandedCategoryIds.has(String(mc.id));

            return (
              <div key={mc.id} className="border border-gray-200 rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => toggleExpanded(mc.id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                  disabled={isSaving}
                >
                  <div className="text-left">
                    <div className="text-sm font-semibold text-gray-900">{mc.name}</div>
                    <div className="text-xs text-gray-500">{children.length} item(s)</div>
                  </div>
                  <div className="text-gray-500 text-sm">{isExpanded ? '−' : '+'}</div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4">
                    {children.length === 0 ? (
                      <div className="text-sm text-gray-500 italic">No children yet.</div>
                    ) : (
                      <div className="space-y-3">
                        {[...children]
                          .sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0))
                          .map((ch, idx) => (
                            <DraggableChildItem
                              key={ch.id}
                              child={ch}
                              categoryId={mc.id}
                              index={idx}
                              updateChildField={updateChildField}
                              deleteChild={deleteChild}
                              moveChild={moveChild}
                              isSaving={isSaving}
                            />
                          ))}
                      </div>
                    )}
                    <div className="flex justify-end mt-3">
                      <button
                        type="button"
                        onClick={() => addChild(mc.id)}
                        className="text-sm bg-primary text-white px-3 py-1 rounded-md hover:bg-gray-800 transition-colors"
                        disabled={isSaving}
                      >
                        Add Child
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
  );

  if (embedded) return content;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      showActionButtons={true}
      submitButtonText={isSaving ? 'Saving...' : 'Save'}
      onSubmit={handleSave}
      submitDisabled={!canSave}
      size="xl"
      width="800px"
    >
      {content}
    </Drawer>
  );
};

